import { FilePicker } from "@/components/FilePicker";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi } from "@tanstack/react-router";
import {
  ApiError,
  getMember,
  getMemberPhoto,
  updateMember,
  uploadMemberPhoto,
  type MemberDetail,
  type MemberUpdate,
} from "@/api/client";
import { useSession } from "@/auth/session";
import { formatDate } from "@/components/format";
import { ScrollArea } from "@/components/ScrollArea";
import { DetailField, EmptyState, ErrorBanner, StatusBadge, ToolbarButton, WorkspaceHeader } from "@/components/ui";

const detailApi = getRouteApi("/staff/members/$memberId");

export function MemberDetailPage() {
  const { memberId } = detailApi.useParams();
  const member = useQuery({ queryKey: ["member", memberId], queryFn: () => getMember(memberId) });

  // 404 is an unknown id, 422 a malformed one; both mean the link points at nothing.
  if (member.error instanceof ApiError && [404, 422].includes(member.error.status)) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="Member not found" detail="No member matches this link." />
        <Link to="/members" className="text-sm font-medium text-zinc-950 underline">
          Search members
        </Link>
      </section>
    );
  }
  if (!member.data) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="Member" detail={memberId} />
        <ErrorBanner error={member.error} />
        {member.isLoading ? <EmptyState title="Loading member" detail="Fetching details." /> : null}
      </section>
    );
  }
  return <MemberView member={member.data} />;
}

function MemberView({ member }: { member: MemberDetail }) {
  const { can } = useSession();
  const [editing, setEditing] = useState(false);
  return (
    <section className="space-y-5">
      <WorkspaceHeader
        title={member.full_name}
        detail={member.membership_no ?? "No membership number yet"}
        action={
          can("member.manage") && !editing ? (
            <ToolbarButton onClick={() => setEditing(true)}>Edit details</ToolbarButton>
          ) : null
        }
      />
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
        <CertificateHistory member={member} />
        <div className="h-fit space-y-4">
          {editing ? (
            <EditMemberForm member={member} onDone={() => setEditing(false)} />
          ) : (
            <div className="rounded-lg border border-zinc-200 bg-white p-4">
              <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm md:grid-cols-2">
                <DetailField label="Email" value={member.email ?? "-"} />
                <DetailField label="Phone" value={member.phone ?? "-"} />
                <DetailField label="External ref" value={member.external_ref ?? "-"} mono />
                <DetailField label="Added" value={formatDate(member.created_at)} />
              </dl>
            </div>
          )}
          <MemberPhoto member={member} />
        </div>
      </div>
    </section>
  );
}

function EditMemberForm({ member, onDone }: { member: MemberDetail; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<MemberUpdate>({
    full_name: member.full_name,
    email: member.email ?? "",
    phone: member.phone ?? "",
    external_ref: member.external_ref ?? "",
  });
  const save = useMutation({
    mutationFn: () => updateMember(member.id, form),
    onSuccess: async (updated) => {
      queryClient.setQueryData(["member", member.id], updated);
      await queryClient.invalidateQueries({ queryKey: ["members"] });
      onDone();
    },
  });
  const input = (key: keyof MemberUpdate, label: string) => (
    <label className="block text-sm">
      <span className="font-medium text-zinc-700">{label}</span>
      <input
        value={form[key] ?? ""}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="mt-1 w-full rounded-md border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
      />
    </label>
  );
  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      {input("full_name", "Full name")}
      {input("email", "Email")}
      {input("phone", "Phone")}
      {input("external_ref", "External ref")}
      <p className="text-xs text-zinc-500">Membership numbers are assigned by batches and cannot be edited here.</p>
      <ErrorBanner error={save.error} />
      <div className="flex gap-2">
        <ToolbarButton tone="primary" disabled={save.isPending || form.full_name.trim() === ""} onClick={() => save.mutate()}>
          Save
        </ToolbarButton>
        <ToolbarButton disabled={save.isPending} onClick={onDone}>
          Cancel
        </ToolbarButton>
      </div>
    </div>
  );
}

function MemberPhoto({ member }: { member: MemberDetail }) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const photo = useQuery({
    queryKey: ["member-photo", member.id],
    queryFn: () => getMemberPhoto(member.id),
    enabled: member.has_photo,
  });
  const [photoURL, setPhotoURL] = useState<string | null>(null);
  useEffect(() => {
    if (!photo.data) return;
    const url = URL.createObjectURL(photo.data);
    setPhotoURL(url);
    return () => URL.revokeObjectURL(url);
  }, [photo.data]);

  const upload = useMutation({
    mutationFn: (chosen: File) => uploadMemberPhoto(member.id, chosen),
    onSuccess: async (updated) => {
      setFile(null);
      queryClient.setQueryData(["member", member.id], updated);
      await queryClient.invalidateQueries({ queryKey: ["member-photo", member.id] });
    },
  });

  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 className="text-sm font-semibold">Photo</h2>
      {member.has_photo && photoURL ? (
        <img src={photoURL} alt={`Photo of ${member.full_name}`} className="h-40 w-32 rounded-md object-cover" />
      ) : (
        <p className="text-sm text-zinc-500">{member.has_photo ? "Loading photo..." : "No photo yet."}</p>
      )}
      <ErrorBanner error={photo.error ?? upload.error} />
      {can("member.manage") ? (
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-64 flex-1">
            <FilePicker
              label="Photo file"
              accept="image/png,image/jpeg"
              extensions={[".png", ".jpg", ".jpeg"]}
              typeError="Only PNG or JPEG images can be uploaded"
              maxBytes={5 << 20}
              hint="PNG or JPEG, up to 5 MB"
              thumbnail
              value={file}
              onChange={setFile}
            />
          </div>
          <ToolbarButton disabled={!file || upload.isPending} onClick={() => file && upload.mutate(file)}>
            {upload.isPending ? "Uploading..." : "Upload photo"}
          </ToolbarButton>
        </div>
      ) : null}
    </div>
  );
}

function CertificateHistory({ member }: { member: MemberDetail }) {
  const { can } = useSession();
  return (
    <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
      <h2 className="border-b border-zinc-200 px-4 py-3 text-sm font-semibold">Certificates</h2>
      {member.certificates.length === 0 ? (
        <EmptyState title="No certificates yet" detail="Certificates appear here once a batch is approved." />
      ) : (
        <ScrollArea label="Certificate history">
          <table className="w-full text-left text-sm">
            <tbody className="divide-y divide-zinc-100">
              {member.certificates.map((cert) => (
                <tr key={cert.id}>
                  <td className="px-4 py-3 font-mono text-xs">
                    {can("certificate.read") ? (
                      <Link to="/certificates/$certificateId" params={{ certificateId: cert.id }} className="underline">
                        {cert.doc_control_id}
                      </Link>
                    ) : (
                      cert.doc_control_id
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-700">{cert.program_name ?? "-"}</td>
                  <td className="px-4 py-3 text-zinc-500">{formatDate(cert.issued_at)}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={cert.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>
      )}
    </div>
  );
}
